import type { AirHandlerProductType, CondenserProductType } from 'data/types';
import {
  isEquipmentPlaceholderProduct,
  isPackagedUnitSectionProduct
} from 'utils/equipment-placeholder';
import type { ExistingEquipmentSizeType } from 'utils/existing-equipment-size';
import type {
  MatchSideRule,
  ZoneMatchSideRules
} from 'utils/zone-match-labels';

const BTU_PER_TON = 12000;

export type ZoneSideCapacities = {
  heating: number;
  cooling: number;
};

type OutdoorHeatPumpHints = Pick<
  CondenserProductType,
  'name' | 'description' | 'series' | 'seriesCode' | 'model'
>;

type ZoneForMatchRules = {
  selectedProduct?: AirHandlerProductType | null;
  blowerProduct?: AirHandlerProductType | null;
};

export function nominalTons(capacityBtu: number): number {
  return Math.round((capacityBtu / BTU_PER_TON) * 2) / 2;
}

export function indoorHeatingRidesOutdoor(
  zone: ZoneForMatchRules,
  outdoor?: OutdoorHeatPumpHints
): boolean {
  if ((zone.selectedProduct?.heatingBTU ?? 0) <= 0) {
    return false;
  }
  if (/coil/i.test(zone.selectedProduct?.type ?? '')) {
    return true;
  }
  const haystack = [
    outdoor?.name,
    outdoor?.description,
    outdoor?.series,
    outdoor?.seriesCode,
    outdoor?.model
  ]
    .filter(Boolean)
    .join(' ');
  return /heat\s*pump/i.test(haystack);
}

function outdoorUnitAtLeastExisting(
  coolingCapacity: number,
  existingTons: number | null | undefined
): boolean {
  return (
    coolingCapacity > 0 &&
    existingTons != null &&
    nominalTons(coolingCapacity) >= existingTons
  );
}

function coolingSideRule(
  capacity: number,
  existingTons: number | null | undefined,
  indoorKept: boolean
): MatchSideRule {
  if (capacity > 0) {
    return outdoorUnitAtLeastExisting(capacity, existingTons)
      ? 'notUndersized'
      : 'default';
  }
  return indoorKept ? 'excluded' : 'default';
}

function heatingSideRule(
  capacity: number,
  existingBtu: number | null | undefined,
  heatedByOutdoorUnitAtLeastExisting: boolean,
  keptUnitPresent: boolean
): MatchSideRule {
  if (capacity > 0) {
    const atLeastExistingHeat = existingBtu != null && capacity >= existingBtu;
    return atLeastExistingHeat || heatedByOutdoorUnitAtLeastExisting
      ? 'notUndersized'
      : 'default';
  }
  return keptUnitPresent ? 'excluded' : 'default';
}

// A placeholder whose capacity is unknown, so a side it leaves at zero is
// not judged. The packaged-unit section is the one placeholder that is not:
// its figures are the sold unit's own, and a zero there is a unit with no
// heat, judged like any cooling-only system.
function isKeptOrMissingEquipment(
  product: AirHandlerProductType | null | undefined
): boolean {
  return (
    isEquipmentPlaceholderProduct(product) &&
    !isPackagedUnitSectionProduct(product)
  );
}

export function buildZoneMatchSideRules(
  zone: ZoneForMatchRules,
  capacities: ZoneSideCapacities,
  existingEquipmentSize?: ExistingEquipmentSizeType | null,
  outdoor?: OutdoorHeatPumpHints
): ZoneMatchSideRules {
  const indoorKept = isKeptOrMissingEquipment(zone.selectedProduct);
  const blowerKept = isKeptOrMissingEquipment(zone.blowerProduct);
  return {
    heating: heatingSideRule(
      capacities.heating,
      existingEquipmentSize?.heatingBTU,
      indoorHeatingRidesOutdoor(zone, outdoor) &&
        outdoorUnitAtLeastExisting(
          capacities.cooling,
          existingEquipmentSize?.coolingTons
        ),
      indoorKept || blowerKept
    ),
    cooling: coolingSideRule(
      capacities.cooling,
      existingEquipmentSize?.coolingTons,
      indoorKept
    )
  };
}
